import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Context for tracking uploaded file hashes across all upload components.
 * Used to prevent duplicate file uploads across different fields.
 */
const UploadContext = createContext(null);

// Only these statuses should block reusing a file on another field.
// Rejected / revision_requested hashes remain free to upload elsewhere.
const DUPLICATE_BLOCKING_STATUSES = new Set(['approved', 'pending_review']);

/**
 * Provider component that maintains the set of uploaded file hashes.
 */
export function UploadProvider({ children }) {
  // Map of file hash -> deliverable ID (to know which field has the file)
  const [uploadedHashes, setUploadedHashes] = useState(new Map());
  
  /**
   * Initialize hashes from existing project data (for files already uploaded to server)
   * @param {Array} deliverables - The deliverables array from project data
   */
  const initializeFromProject = useCallback((deliverables) => {
    if (!deliverables || !Array.isArray(deliverables)) return;
    
    const hashMap = new Map();
    for (const deliverable of deliverables) {
      if (
        deliverable.file_hash &&
        DUPLICATE_BLOCKING_STATUSES.has(deliverable.status)
      ) {
        hashMap.set(deliverable.file_hash, deliverable.id);
      }
    }
    setUploadedHashes(hashMap);
  }, []);

  /**
   * Register a file hash as uploaded for a specific deliverable
   * @param {string} hash - The file content hash
   * @param {string} deliverableId - The ID of the deliverable being uploaded to
   */
  const registerUpload = useCallback((hash, deliverableId) => {
    setUploadedHashes(prev => {
      const next = new Map(prev);
      next.set(hash, deliverableId);
      return next;
    });
  }, []);

  /**
   * Remove a file hash (e.g., when replacing an upload)
   * @param {string} deliverableId - The ID of the deliverable to clear
   */
  const clearUpload = useCallback((deliverableId) => {
    setUploadedHashes(prev => {
      const next = new Map(prev);
      // Find and remove the hash for this deliverable
      for (const [hash, id] of next.entries()) {
        if (id === deliverableId) {
          next.delete(hash);
          break;
        }
      }
      return next;
    });
  }, []);

  /**
   * Check if a file hash has already been uploaded
   * @param {string} hash - The file content hash to check
   * @param {string} currentDeliverableId - The current deliverable (to exclude from check)
   * @returns {boolean} - True if this hash exists for a different deliverable
   */
  const isDuplicate = useCallback((hash, currentDeliverableId) => {
    const existingDeliverableId = uploadedHashes.get(hash);
    // It's a duplicate if the hash exists AND it's for a different deliverable
    return existingDeliverableId !== undefined && existingDeliverableId !== currentDeliverableId;
  }, [uploadedHashes]);

  /**
   * Reset all tracked uploads (e.g., when navigating away)
   */
  const resetUploads = useCallback(() => {
    setUploadedHashes(new Map());
  }, []);

  const value = {
    registerUpload,
    clearUpload,
    isDuplicate,
    resetUploads,
    initializeFromProject
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
}

/**
 * Hook to access the upload context
 * @returns {Object} - The upload context with registerUpload, clearUpload, isDuplicate, resetUploads
 */
export function useUploadContext() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }
  return context;
}
